import { backendConfig } from '../config/backendConfig';
import * as Sentry from '@sentry/react';

// Lightweight, environment-aware observability helpers.
// In dev: verbose console logging.
// In staging/prod: sampled console logging + optional Sentry sink.

const ENV = backendConfig.environment || 'production';
const isDev = ENV === 'development';
const isStaging = ENV === 'staging';
const isProd = ENV === 'production' || ENV === 'prod';

const BASE_META = {
  env: ENV,
};

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENABLED = !!SENTRY_DSN && (isStaging || isProd);

if (SENTRY_ENABLED) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENV,
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.2),
    sendDefaultPii: false,
    beforeSend(event) {
      // Scrub obvious PII fields
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
      }
      if (event.request && event.request.headers) {
        delete event.request.headers.authorization;
      }
      return event;
    },
  });
}

export function trackEvent(name, props = {}) {
  if (!name) return;

  const payload = {
    type: 'event',
    name,
    ...BASE_META,
    ...props,
    ts: new Date().toISOString(),
  };

  if (isDev) {
    // Full fidelity in development
    // eslint-disable-next-line no-console
    console.info('📊 [analytics]', payload);
    return;
  }

  if (isStaging || isProd) {
    // Keep it lightweight: sample logs to console to avoid noisy output.
    if (Math.random() < 0.2) {
      // eslint-disable-next-line no-console
      console.info('📊 [analytics]', payload);
    }
    // Hook point: send to backend/SaaS (Sentry, GA, etc.) if needed.
  }
}

export function logError(error, context = {}) {
  const baseError =
    error instanceof Error
      ? { message: error.message, stack: error.stack }
      : { message: String(error) };

  const payload = {
    type: 'error',
    ...BASE_META,
    ...context,
    ...baseError,
    ts: new Date().toISOString(),
  };

  // Always log errors to console, but keep structured.
  // eslint-disable-next-line no-console
  console.error('🚨 [error]', payload);

  if (SENTRY_ENABLED) {
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        env: ENV,
      },
      extra: context,
    });
  }
}

